import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  Permission,
  ReachProSupportCaseClient,
} from 'src/WebApiController';

import { flattenSupportCasePages } from './reachProSupportCasePagination';

export const SUPPORT_CASES_QUERY_KEY = 'reachpro-support-cases';
const PAGE_SIZE = 50;

export const useReachProSupportCases = () => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const { pathname } = useLocation();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);
  const hasSupportViewPermission = useUserHasAnyOfPermissions(
    Permission.SellerSupportCase_View
  );

  const isOnReachProPage = pathname.startsWith('/reachpro-support');
  const shouldQuery =
    hasReachProSupportFeature &&
    hasSupportViewPermission &&
    activeAccountWebClientConfig.activeAccountId != null &&
    isOnReachProPage;

  const query = useInfiniteQuery({
    queryKey: [
      SUPPORT_CASES_QUERY_KEY,
      activeAccountWebClientConfig.activeAccountId,
    ],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      if (!shouldQuery) {
        return null;
      }
      return tryInvokeApi(
        async () => {
          return await new ReachProSupportCaseClient(
            activeAccountWebClientConfig
          ).getSupportCases(pageParam, PAGE_SIZE, undefined);
        },
        (error) => {
          trackError('ReachProSupportCaseClient.getSupportCases', error);
        }
      );
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.hasMore) {
        return undefined;
      }
      return allPages.reduce(
        (total, page) => total + (page?.items.length ?? 0),
        0
      );
    },
    enabled: shouldQuery,
    staleTime: Infinity,
    refetchOnWindowFocus: 'always',
    refetchOnMount: 'always',
  });

  const supportCases = useMemo(
    () => flattenSupportCasePages(query.data?.pages ?? []),
    [query.data]
  );

  return {
    supportCases,
    isLoading: query.isLoading,
    isError: query.isError,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
};
